// ProgressView.jsx - With rankings and sorting by progress
import React, { useState } from 'react';
import { 
  Search, TrendingUp, Award, Clock, CheckCircle, 
  XCircle, Download, ChevronDown, ChevronRight, Medal
} from 'lucide-react';

const ProgressView = ({ users, modules, userProgress, onRefresh, setError }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});

  // Define helper functions FIRST before using them
  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUserProgress = (userId) => {
    if (!userProgress || Object.keys(userProgress).length === 0) {
      return 0;
    }

    const progress = userProgress[userId];
    if (!progress || !modules || modules.length === 0) return 0;
    
    const completedModules = (progress.completed_modules || []).filter(moduleId => 
      modules.some(m => m.id === moduleId)
    ).length;
    
    return Math.round((completedModules / modules.length) * 100);
  };

  const getUserModuleProgress = (userId, moduleId) => {
    if (!userProgress || Object.keys(userProgress).length === 0) {
      return { completed: false, chaptersCompleted: 0, totalChapters: 0, percentage: 0 };
    }

    const progress = userProgress[userId];
    if (!progress || !progress.modules) {
      return { completed: false, chaptersCompleted: 0, totalChapters: 0, percentage: 0 };
    }
    
    const moduleProgress = progress.modules[moduleId] || {};
    const module = modules.find(m => m.id === moduleId);
    const totalChapters = moduleProgress.total_chapters || module?.chapter_count || 0;
    const chaptersCompleted = (moduleProgress.chapters_completed || []).length;
    
    return {
      completed: (progress.completed_modules || []).includes(moduleId),
      chaptersCompleted,
      totalChapters,
      percentage: totalChapters > 0 ? Math.round((chaptersCompleted / totalChapters) * 100) : 0
    };
  };

  const toggleModuleExpansion = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleExportProgress = () => {
    // TODO: Implement CSV export functionality
    console.log('Export progress data');
  };

  // Get medal icon and color based on rank
  const getRankMedal = (rank) => {
    if (rank === 1) {
      return { icon: '🥇', color: 'text-yellow-500', bg: 'bg-yellow-50' };
    } else if (rank === 2) {
      return { icon: '🥈', color: 'text-gray-400', bg: 'bg-gray-50' };
    } else if (rank === 3) {
      return { icon: '🥉', color: 'text-orange-600', bg: 'bg-orange-50' };
    }
    return null;
  };

  // Filter and sort users
  const filteredAndSortedUsers = users
    .filter(user => {
      if (user.is_admin) return false;
      
      const matchesSearch = (
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      const progress = getUserProgress(user.id);
      const matchesStatus = 
        filterStatus === 'all' ||
        (filterStatus === 'completed' && progress === 100) ||
        (filterStatus === 'in-progress' && progress > 0 && progress < 100) ||
        (filterStatus === 'not-started' && progress === 0);
      
      return matchesSearch && matchesStatus;
    })
    // Sort by progress (highest first)
    .sort((a, b) => {
      const progressA = getUserProgress(a.id);
      const progressB = getUserProgress(b.id);
      
      // First, sort by progress percentage
      if (progressB !== progressA) {
        return progressB - progressA;
      }
      
      // If progress is equal, sort alphabetically by name
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

  // Check if we have data
  const hasProgressData = userProgress && Object.keys(userProgress).length > 0;
  const hasUsers = users && users.length > 0;
  const hasModules = modules && modules.length > 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Seguimiento de Progreso</h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Monitorea el avance de los estudiantes en todos los módulos
          </p>
        </div>
        
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar estudiantes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            />
          </div>
          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
          >
            <option value="all">Todos los estados</option>
            <option value="completed">Completado (100%)</option>
            <option value="in-progress">En progreso</option>
            <option value="not-started">Sin comenzar</option>
          </select>
        </div>
      </div>

      {/* Progress Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-xs sm:text-sm">Total Estudiantes</span>
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-gray-900">
            {users.filter(u => !u.is_admin).length}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-xs sm:text-sm">Completaron Todo</span>
            <Award className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-gray-900">
            {users.filter(u => !u.is_admin && getUserProgress(u.id) === 100).length}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-xs sm:text-sm">En Progreso</span>
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-gray-900">
            {users.filter(u => {
              if (u.is_admin) return false;
              const progress = getUserProgress(u.id);
              return progress > 0 && progress < 100;
            }).length}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-xs sm:text-sm">Sin Comenzar</span>
            <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-gray-900">
            {users.filter(u => !u.is_admin && getUserProgress(u.id) === 0).length}
          </div>
        </div>
      </div>

      {/* Top 3 Leaderboard - Only show if there's progress data */}
      {hasProgressData && filteredAndSortedUsers.length >= 3 && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-200 p-4 sm:p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Award className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Top 3 Estudiantes</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {filteredAndSortedUsers.slice(0, 3).map((user, index) => {
              const rank = index + 1;
              const medal = getRankMedal(rank);
              const progress = getUserProgress(user.id);
              
              return (
                <div key={user.id} className={`${medal.bg} rounded-lg p-4 border-2 ${
                  rank === 1 ? 'border-yellow-300' : rank === 2 ? 'border-gray-300' : 'border-orange-300'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className="text-3xl sm:text-4xl">{medal.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className={`text-lg sm:text-xl font-bold ${medal.color}`}>#{rank}</span>
                        <span className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                          {user.first_name} {user.last_name}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center space-x-2">
                        <div className="flex-1 bg-white rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : 'bg-orange-500'
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-900">{progress}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Users Progress List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {!hasUsers || !hasModules ? (
          <div className="text-center py-12 px-4">
            <TrendingUp className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              {!hasModules ? 'No hay módulos creados' : 'No hay usuarios registrados'}
            </h3>
            <p className="text-sm sm:text-base text-gray-500">
              {!hasModules 
                ? 'Crea módulos primero para ver el progreso de los estudiantes'
                : 'Registra usuarios para comenzar a monitorear su progreso'
              }
            </p>
          </div>
        ) : filteredAndSortedUsers.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredAndSortedUsers.map((user, index) => {
              const rank = index + 1;
              const overallProgress = getUserProgress(user.id);
              const isExpanded = selectedUser === user.id;
              const medal = getRankMedal(rank);
              
              return (
                <div key={user.id} className={`hover:bg-gray-50 transition-colors ${
                  medal ? medal.bg : ''
                }`}>
                  {/* User Header */}
                  <div 
                    className="p-4 sm:p-6 cursor-pointer"
                    onClick={() => setSelectedUser(isExpanded ? null : user.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                        {/* Rank Badge */}
                        <div className="flex-shrink-0">
                          {medal ? (
                            <div className="text-2xl sm:text-3xl">{medal.icon}</div>
                          ) : (
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-100 flex items-center justify-center">
                              <span className="text-sm sm:text-base font-bold text-gray-600">#{rank}</span>
                            </div>
                          )}
                        </div>

                        {/* User Avatar */}
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                          {getInitials(user.first_name, user.last_name)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">
                              {user.first_name} {user.last_name}
                            </h3>
                            <span className={`inline-flex px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full flex-shrink-0 ${
                              overallProgress === 100 
                                ? 'bg-green-100 text-green-800' 
                                : overallProgress > 0
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {overallProgress === 100 ? 'Completado' : overallProgress > 0 ? 'En progreso' : 'Sin comenzar'}
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-500 truncate hidden sm:block">{user.email}</p>
                          
                          {/* Progress Bar */}
                          <div className="mt-2 sm:mt-3 flex items-center space-x-2 sm:space-x-3">
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5 sm:h-2">
                              <div 
                                className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                                  overallProgress === 100 ? 'bg-green-600' : 
                                  medal ? (rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : 'bg-orange-500') :
                                  'bg-blue-600'
                                }`}
                                style={{ width: `${overallProgress}%` }}
                              />
                            </div>
                            <span className="text-xs sm:text-sm font-medium text-gray-900 w-8 sm:w-12 text-right flex-shrink-0">
                              {overallProgress}%
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 sm:space-x-4 ml-2">
                        <div className="text-right hidden lg:block">
                          <div className="text-xs sm:text-sm text-gray-500">Último acceso</div>
                          <div className="text-xs sm:text-sm font-medium text-gray-900">
                            {formatDate(user.last_login)}
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Module Details */}
                  {isExpanded && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 bg-gray-50">
                      <div className="space-y-3">
                        <h4 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                          Progreso por Módulo
                        </h4>
                        
                        {modules.length > 0 ? (
                          modules.map(module => {
                            const moduleProgress = getUserModuleProgress(user.id, module.id);
                            
                            return (
                              <div key={module.id} className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200">
                                <div className="flex items-center justify-between mb-2 gap-3">
                                  <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                      moduleProgress.completed 
                                        ? 'bg-green-100' 
                                        : moduleProgress.percentage > 0 
                                        ? 'bg-blue-100' 
                                        : 'bg-gray-100'
                                    }`}>
                                      {moduleProgress.completed ? (
                                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                                      ) : (
                                        <span className={`text-xs font-semibold ${
                                          moduleProgress.percentage > 0 ? 'text-blue-600' : 'text-gray-400'
                                        }`}>
                                          {module.id}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h5 className="font-medium text-gray-900 text-sm sm:text-base truncate">{module.title}</h5>
                                      <p className="text-xs text-gray-500">
                                        {moduleProgress.chaptersCompleted} de {moduleProgress.totalChapters} capítulos
                                      </p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center space-x-2 flex-shrink-0">
                                    <div className="w-16 sm:w-24 bg-gray-200 rounded-full h-1.5 sm:h-2">
                                      <div 
                                        className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                                          moduleProgress.completed ? 'bg-green-600' : 'bg-blue-600'
                                        }`}
                                        style={{ width: `${moduleProgress.percentage}%` }}
                                      />
                                    </div>
                                    <span className="text-xs sm:text-sm font-medium text-gray-900 w-8 sm:w-10 text-right">
                                      {moduleProgress.percentage}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-4 text-sm text-gray-500">
                            No hay módulos disponibles
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 px-4">
            <TrendingUp className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              No se encontraron estudiantes
            </h3>
            <p className="text-sm sm:text-base text-gray-500">
              Intenta cambiar los filtros de búsqueda
            </p>
          </div>
        )}
      </div>

      {/* Debug Info - Remove in production */}
      {!hasProgressData && hasUsers && hasModules && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
          <p className="text-yellow-800 font-medium">⚠️ Nota: No hay datos de progreso disponibles todavía.</p>
          <p className="text-yellow-700 mt-1">Los estudiantes deben comenzar a completar capítulos para que aparezca su progreso aquí.</p>
        </div>
      )}
    </div>
  );
};

export default ProgressView;